/**
 * CB Salon Checkout — In-salon tablet checkout for Colour Blend by Catherine Boden
 * v1 — 2026-05-03
 *
 * Loaded by sections/cb-salon-checkout.liquid on /pages/checkout
 * Page setup: PIN-protected, tablet-optimised React app.
 *
 * Flow:
 *   pin -> stylist -> today (calendar fetch via CB-55) -> basket -> confirm (post via CB-54)
 *
 * Brand:
 *   Aubergine #4a1942, Gold #8B734A, Cream #F6EFED / #F7F2EB, Charcoal #2A2131
 *   Cormorant Garamond (display), Jost (body)
 *
 * Globals (set by the Liquid section):
 *   window.__CB_CHECKOUT_PIN, window.__CB_WEBHOOKS, window.__CB_PRODUCTS
 */
(function () {
  'use strict';

  var e = React.createElement;
  var useState = React.useState;
  var useEffect = React.useEffect;
  var useMemo = React.useMemo;
  var useRef = React.useRef;

  // ----- Config -----
  var PIN = String(window.__CB_CHECKOUT_PIN || '2468');
  var WEBHOOKS = window.__CB_WEBHOOKS || {
    calendar: 'https://n8n.catherineboden.com/webhook/cb-checkout-calendar',
    checkout: 'https://n8n.catherineboden.com/webhook/cb-checkout'
  };
  var PRODUCTS = (window.__CB_PRODUCTS || []).filter(function (p) { return p && p.available !== false; });

  // ----- Brand -----
  var C = {
    aubergine: '#4a1942',
    gold: '#8B734A',
    goldLight: '#c9a96e',
    cream: '#F6EFED',
    creamWarm: '#F7F2EB',
    charcoal: '#2A2131',
    body: '#5C5158',
    muted: '#9B8E8A',
    line: 'rgba(139,115,74,0.18)',
    white: '#FFFFFF',
    success: '#3F7A5C',
    danger: '#A33A3A'
  };
  var FONT_DISPLAY = "'Cormorant Garamond', Georgia, serif";
  var FONT_BODY = "'Jost', system-ui, -apple-system, sans-serif";

  // Stylist accent badges (small dot only)
  var STYLIST_ACCENT = {
    'Catherine': '#F5D76E',
    'Amber': '#89CFF0',
    'Jess L': '#F4A6C1',
    'Jess S': '#C3A6D8'
  };

  var STYLISTS = [
    { name: 'Catherine', role: 'Director & Creator of HCB®' },
    { name: 'Amber', role: 'Senior Colourist' },
    { name: 'Jess L', role: 'Senior Colourist' },
    { name: 'Jess S', role: 'Stylist' }
  ];

  // Service menu — source of truth: sections/cb-services.liquid preset.
  // null = price on consultation. Missing stylist key = stylist doesn't perform.
  var SERVICE_MENU = [
    { type: 'Full Works',                  group: 'Colour',     from: { Catherine: 350, Amber: 295, 'Jess L': 295 } },
    { type: 'Refresh + Highlights',        group: 'Colour',     from: { Catherine: 395, Amber: 325, 'Jess L': 325 } },
    { type: 'Refresh',                     group: 'Colour',     from: { Catherine: 325, Amber: 225, 'Jess L': 225 } },
    { type: 'Colour Correction',           group: 'Colour',     from: { Catherine: null, Amber: null, 'Jess L': null } },
    { type: 'Full Head Colour',            group: 'Colour',     from: { Catherine: 200, Amber: 180, 'Jess L': 180 } },
    { type: 'Toner',                       group: 'Colour',     from: { Catherine: 95, Amber: 95, 'Jess L': 85 } },
    { type: 'Roots & Toner',               group: 'Colour',     from: { Catherine: 180, Amber: 180, 'Jess L': 160 } },
    { type: 'Roots, Whites',               group: 'Colour',     from: { Catherine: 100, Amber: 100, 'Jess L': 90 } },
    { type: 'Cut & Blow Dry',              group: 'Cutting',    from: { Catherine: 150, Amber: 100, 'Jess L': 100, 'Jess S': 100 } },
    { type: 'Wash & Blow Dry',             group: 'Cutting',    from: { Catherine: 55, Amber: 55, 'Jess S': 45 } },
    { type: 'Curling / Styling',           group: 'Cutting',    from: { Catherine: 30, Amber: 30, 'Jess L': 30, 'Jess S': 30 } },
    { type: 'Gloss',                       group: 'Additional', from: { Catherine: 40, Amber: 40, 'Jess L': 40, 'Jess S': 40 } },
    { type: 'Extensions Fitting',          group: 'Additional', from: { Amber: null, 'Jess L': null }, note: '£85 per pack, on consultation' },
    { type: 'Extension Removal & Fitting', group: 'Additional', from: { Amber: 80, 'Jess L': 80 } },
    { type: 'HCB Model',                   group: 'Additional', from: { Catherine: 180, Amber: 180, 'Jess L': 180, 'Jess S': 180 } },
    { type: 'Japanese Head Spa',           group: 'Additional', from: {}, note: 'Enquire' },
    { type: 'Skin Specialist Treatments',  group: 'Additional', from: {}, note: 'Enquire' }
  ];

  // ----- Utils -----
  function todayISO() {
    var d = new Date();
    var pad = function (n) { return String(n).padStart(2, '0'); };
    return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
  }
  function fmtMoney(n) {
    if (n == null || isNaN(n)) return '—';
    return '£' + Number(n).toFixed(2);
  }
  function fmtTime(iso) {
    if (!iso) return '';
    var d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    var h = d.getHours();
    var m = d.getMinutes();
    var ampm = h >= 12 ? 'pm' : 'am';
    var h12 = h > 12 ? h - 12 : (h === 0 ? 12 : h);
    return h12 + ':' + String(m).padStart(2, '0') + ampm;
  }
  function fmtDateLong(iso) {
    if (!iso) return '';
    var months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
    var days = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
    var p = iso.split('-').map(Number);
    var d = new Date(Date.UTC(p[0], p[1]-1, p[2]));
    return days[d.getUTCDay()] + ' ' + p[2] + ' ' + months[p[1]-1] + ' ' + p[0];
  }
  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      if (!r.ok) return r.text().then(function (t) { throw new Error('HTTP ' + r.status + ' ' + (t || '').slice(0, 200)); });
      return r.json();
    });
  }
  function menuPrice(serviceType, stylist) {
    var s = SERVICE_MENU.find(function (x) { return x.type === serviceType; });
    if (!s) return null;
    if (Object.prototype.hasOwnProperty.call(s.from, stylist)) return s.from[stylist];
    return null;
  }
  function servicesForStylist(stylist) {
    return SERVICE_MENU.filter(function (s) { return Object.prototype.hasOwnProperty.call(s.from, stylist); });
  }

  // ----- Inject one global stylesheet for shared chrome -----
  (function injectCSS() {
    var css =
      "*,*:before,*:after{box-sizing:border-box}" +
      ".cb-co{font-family:" + FONT_BODY + ";color:" + C.charcoal + "}" +
      ".cb-co h1,.cb-co h2,.cb-co h3{font-family:" + FONT_DISPLAY + ";font-weight:400;letter-spacing:0;margin:0}" +
      ".cb-co button{font-family:" + FONT_BODY + ";cursor:pointer}" +
      ".cb-co input,.cb-co select,.cb-co textarea{font-family:" + FONT_BODY + ";font-size:16px;color:" + C.charcoal + "}" +
      ".cb-co input:focus,.cb-co select:focus,.cb-co textarea:focus{outline:2px solid " + C.gold + ";outline-offset:1px}" +
      ".cb-co a{color:" + C.gold + "}" +
      ".cb-co ::placeholder{color:" + C.muted + "}" +
      ".cb-co .label{font-family:" + FONT_BODY + ";font-size:10px;letter-spacing:3px;text-transform:uppercase;color:" + C.gold + ";font-weight:500}" +
      ".cb-co .rule{height:1px;background:" + C.line + ";border:none}" +
      ".cb-co .btn{display:inline-flex;align-items:center;justify-content:center;gap:8px;min-height:48px;padding:12px 24px;border-radius:4px;font-size:12px;letter-spacing:2px;text-transform:uppercase;font-weight:500;border:1px solid " + C.gold + ";background:" + C.gold + ";color:#fff;transition:opacity .2s,transform .1s}" +
      ".cb-co .btn:hover{opacity:.9}" +
      ".cb-co .btn:active{transform:translateY(1px)}" +
      ".cb-co .btn[disabled]{opacity:.4;cursor:not-allowed}" +
      ".cb-co .btn.ghost{background:transparent;color:" + C.gold + "}" +
      ".cb-co .btn.dark{background:" + C.aubergine + ";border-color:" + C.aubergine + "}" +
      ".cb-co .btn.danger{background:transparent;color:" + C.danger + ";border-color:" + C.danger + "}" +
      ".cb-co .btn.lg{min-height:64px;padding:18px 32px;font-size:13px}" +
      ".cb-co .card{background:#fff;border-radius:6px;padding:20px;box-shadow:0 1px 3px rgba(74,25,66,.06);border:1px solid " + C.line + "}" +
      ".cb-co .card.tap{cursor:pointer;transition:box-shadow .2s,transform .1s}" +
      ".cb-co .card.tap:hover{box-shadow:0 6px 18px rgba(74,25,66,.1)}" +
      ".cb-co .card.tap:active{transform:translateY(1px)}" +
      ".cb-co .field{display:flex;flex-direction:column;gap:6px;margin-bottom:18px}" +
      ".cb-co .field label{font-size:10px;letter-spacing:2px;text-transform:uppercase;color:" + C.gold + ";font-weight:500}" +
      ".cb-co .field input,.cb-co .field select,.cb-co .field textarea{padding:14px 14px;border:1px solid " + C.line + ";border-radius:4px;background:#fff}" +
      ".cb-co .field .help{font-size:12px;color:" + C.muted + ";margin-top:2px}" +
      ".cb-co .pad{padding:32px}" +
      "@media (max-width:640px){.cb-co .pad{padding:20px}}";
    var s = document.createElement('style');
    s.id = 'cb-co-styles';
    s.textContent = css;
    document.head.appendChild(s);
  })();

  // =======================================================
  //   PIN screen
  // =======================================================
  function PinScreen(props) {
    var stateE = useState('');
    var entered = stateE[0], setEntered = stateE[1];
    var stateError = useState(false);
    var error = stateError[0], setError = stateError[1];

    function onDigit(d) {
      if (entered.length >= 4) return;
      var next = entered + d;
      setEntered(next); setError(false);
      if (next.length === 4) {
        setTimeout(function () {
          if (next === PIN) props.onUnlock();
          else { setError(true); setEntered(''); }
        }, 80);
      }
    }
    function onBack() { setEntered(entered.slice(0, -1)); setError(false); }
    function onClear() { setEntered(''); setError(false); }

    var keys = ['1','2','3','4','5','6','7','8','9','clear','0','back'];
    return e('div', { className: 'cb-co', style: { minHeight: '100vh', background: C.cream, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 } },
      e('div', { style: { width: '100%', maxWidth: 380, textAlign: 'center' } },
        e('div', { className: 'label', style: { marginBottom: 14 } }, 'Colour Blend'),
        e('h1', { style: { fontSize: 30, color: C.charcoal, marginBottom: 6 } }, 'Salon ', e('em', { style: { color: C.gold, fontStyle: 'italic' } }, 'Checkout')),
        e('p', { style: { color: C.muted, fontSize: 14, fontFamily: FONT_DISPLAY, fontStyle: 'italic', marginBottom: 30 } }, 'Enter staff PIN to begin'),
        e('div', { style: { display: 'flex', justifyContent: 'center', gap: 12, marginBottom: 20 } },
          [0,1,2,3].map(function (i) {
            var filled = entered.length > i;
            return e('div', { key: i, style: { width: 18, height: 18, borderRadius: '50%', border: '1px solid ' + C.gold, background: filled ? C.gold : 'transparent', transition: 'all .15s' } });
          })
        ),
        error && e('div', { style: { color: C.danger, fontSize: 13, marginBottom: 16 } }, 'Incorrect PIN. Try again.'),
        e('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 } },
          keys.map(function (k) {
            var label = k === 'back' ? '⌫' : (k === 'clear' ? 'Clear' : k);
            var action = k === 'back' ? onBack : (k === 'clear' ? onClear : function () { onDigit(k); });
            return e('button', { key: k, onClick: action, style: { minHeight: 70, fontSize: 22, fontFamily: FONT_DISPLAY, color: C.charcoal, background: '#fff', border: '1px solid ' + C.line, borderRadius: 6 } }, label);
          })
        )
      )
    );
  }

  // =======================================================
  //   Stylist picker
  // =======================================================
  function StylistScreen(props) {
    return e('div', { className: 'cb-co pad', style: { minHeight: '100vh', background: C.cream } },
      e('div', { style: { maxWidth: 880, margin: '0 auto' } },
        e('div', { style: { textAlign: 'center', marginBottom: 36 } },
          e('div', { className: 'label', style: { marginBottom: 12 } }, 'Step 1 of 3'),
          e('h1', { style: { fontSize: 32, color: C.charcoal } }, 'Who’s ', e('em', { style: { color: C.gold, fontStyle: 'italic' } }, 'checking out?')),
          e('div', { style: { width: 50, height: 1, background: C.gold, margin: '18px auto', opacity: 0.4 } })
        ),
        e('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 } },
          STYLISTS.map(function (s) {
            return e('button', { key: s.name, onClick: function () { props.onPick(s.name); },
              className: 'card tap',
              style: { padding: 28, textAlign: 'center', cursor: 'pointer', minHeight: 160, border: '1px solid ' + C.line, background: '#fff', borderRadius: 6 } },
              e('div', { style: { width: 12, height: 12, borderRadius: '50%', background: STYLIST_ACCENT[s.name], margin: '0 auto 14px' } }),
              e('div', { style: { fontFamily: FONT_DISPLAY, fontSize: 24, color: C.charcoal, marginBottom: 4 } }, s.name),
              e('div', { style: { fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', color: C.muted } }, s.role)
            );
          })
        )
      )
    );
  }

  // =======================================================
  //   Today's clients
  // =======================================================
  function TodayScreen(props) {
    var stateAppts = useState({ status: 'loading', events: [] });
    var data = stateAppts[0], setData = stateAppts[1];
    var date = todayISO();

    useEffect(function () {
      setData({ status: 'loading', events: [] });
      postJSON(WEBHOOKS.calendar, { stylist: props.stylist, date: date })
        .then(function (r) { setData({ status: 'ok', events: r.events || [] }); })
        .catch(function (err) { setData({ status: 'error', events: [], message: err.message }); });
    }, [props.stylist]);

    return e('div', { className: 'cb-co pad', style: { minHeight: '100vh', background: C.cream, paddingBottom: 100 } },
      e('div', { style: { maxWidth: 720, margin: '0 auto' } },
        e('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 } },
          e('button', { onClick: props.onBack, className: 'btn ghost', style: { minHeight: 40, padding: '8px 16px' } }, '← Back'),
          e('div', { style: { display: 'flex', alignItems: 'center', gap: 10 } },
            e('div', { style: { width: 10, height: 10, borderRadius: '50%', background: STYLIST_ACCENT[props.stylist] } }),
            e('div', { style: { fontFamily: FONT_DISPLAY, fontSize: 20, color: C.charcoal } }, props.stylist)
          )
        ),
        e('div', { style: { textAlign: 'center', marginBottom: 28 } },
          e('div', { className: 'label', style: { marginBottom: 8 } }, 'Step 2 of 3'),
          e('h1', { style: { fontSize: 28, color: C.charcoal } }, 'Today’s ', e('em', { style: { color: C.gold, fontStyle: 'italic' } }, 'clients')),
          e('div', { style: { color: C.muted, fontSize: 14, marginTop: 6 } }, fmtDateLong(date))
        ),
        data.status === 'loading' && e('div', { style: { textAlign: 'center', padding: 40, color: C.muted, fontFamily: FONT_DISPLAY, fontStyle: 'italic' } }, 'Fetching today’s appointments…'),
        data.status === 'error' && e('div', { className: 'card', style: { borderLeft: '3px solid ' + C.danger } },
          e('div', { style: { color: C.danger, fontWeight: 500, marginBottom: 6 } }, 'Could not load calendar'),
          e('div', { style: { fontSize: 13, color: C.muted, marginBottom: 14 } }, data.message || ''),
          e('button', { onClick: function () { setData({ status: 'loading', events: [] }); postJSON(WEBHOOKS.calendar, { stylist: props.stylist, date: date }).then(function (r) { setData({ status: 'ok', events: r.events || [] }); }).catch(function (err) { setData({ status: 'error', events: [], message: err.message }); }); }, className: 'btn ghost' }, 'Retry')
        ),
        data.status === 'ok' && data.events.length === 0 && e('div', { className: 'card', style: { textAlign: 'center', padding: 32, color: C.muted } }, 'No appointments in the calendar for today.'),
        data.status === 'ok' && data.events.length > 0 && e('div', { style: { display: 'flex', flexDirection: 'column', gap: 12 } },
          data.events.map(function (ev) {
            var depositBadge = ev.depositPaid > 0
              ? e('span', { style: { fontSize: 10, letterSpacing: 1.5, textTransform: 'uppercase', color: C.success, background: 'rgba(63,122,92,.1)', padding: '4px 8px', borderRadius: 3 } }, 'Deposit £' + ev.depositPaid)
              : e('span', { style: { fontSize: 10, letterSpacing: 1.5, textTransform: 'uppercase', color: C.muted, background: '#f4eee9', padding: '4px 8px', borderRadius: 3 } }, 'No deposit');
            return e('button', { key: ev.eventId, onClick: function () { props.onPick(ev); }, className: 'card tap', style: { display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer' } },
              e('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 } },
                e('div', null,
                  e('div', { style: { fontFamily: FONT_DISPLAY, fontSize: 22, color: C.charcoal, marginBottom: 4 } }, ev.clientName || 'Unknown'),
                  e('div', { style: { fontSize: 13, color: C.body } }, ev.service || '—'),
                  e('div', { style: { fontSize: 12, color: C.muted, marginTop: 6 } }, fmtTime(ev.startTime) + (ev.bookingRef ? ' · ' + ev.bookingRef : ''))
                ),
                e('div', { style: { textAlign: 'right' } }, depositBadge)
              )
            );
          })
        ),
        e('div', { style: { marginTop: 28, textAlign: 'center' } },
          e('button', { onClick: function () { props.onWalkIn(); }, className: 'btn ghost lg' }, '+ Walk-in / Manual Entry')
        )
      )
    );
  }

  // =======================================================
  //   Basket
  // =======================================================
  function BasketScreen(props) {
    var defaults = props.appointment || {};
    var initialService = defaults.service || (servicesForStylist(props.stylist)[0] || {}).type || '';
    var initialPrice = (function () {
      var p = menuPrice(initialService, props.stylist);
      return p == null ? '' : String(p);
    })();

    var stClient = useState({ name: defaults.clientName || '', email: defaults.clientEmail || '', phone: defaults.clientPhone || '' });
    var client = stClient[0], setClient = stClient[1];

    var stService = useState(initialService);
    var service = stService[0], setService = stService[1];

    var stPrice = useState(initialPrice);
    var price = stPrice[0], setPrice = stPrice[1];

    var stItems = useState([]);
    var items = stItems[0], setItems = stItems[1];

    var stDeposit = useState(String(defaults.depositPaid != null && defaults.depositPaid > 0 ? defaults.depositPaid : 50));
    var deposit = stDeposit[0], setDeposit = stDeposit[1];

    var stPaymentMethod = useState('Bank Transfer');
    var paymentMethod = stPaymentMethod[0], setPaymentMethod = stPaymentMethod[1];

    var stNotes = useState('');
    var notes = stNotes[0], setNotes = stNotes[1];

    var stShowPicker = useState(false);
    var showPicker = stShowPicker[0], setShowPicker = stShowPicker[1];

    var stSubmitting = useState({ status: 'idle' });
    var submit = stSubmitting[0], setSubmit = stSubmitting[1];

    var availableServices = useMemo(function () { return servicesForStylist(props.stylist); }, [props.stylist]);

    // Auto-update price when service changes
    function changeService(newType) {
      setService(newType);
      var p = menuPrice(newType, props.stylist);
      setPrice(p == null ? '' : String(p));
    }

    function addProduct(p) {
      // If already in basket, bump qty
      var existing = items.find(function (i) { return i.id === p.id; });
      if (existing) {
        setItems(items.map(function (i) { return i.id === p.id ? Object.assign({}, i, { qty: i.qty + 1 }) : i; }));
      } else {
        setItems(items.concat([{ id: p.id, name: p.name, price: Number(p.price), qty: 1, image: p.image }]));
      }
      setShowPicker(false);
    }
    function removeProduct(id) { setItems(items.filter(function (i) { return i.id !== id; })); }
    function setQty(id, qty) {
      qty = Math.max(1, Number(qty) || 1);
      setItems(items.map(function (i) { return i.id === id ? Object.assign({}, i, { qty: qty }) : i; }));
    }

    var serviceP = Number(price) || 0;
    var productTotal = items.reduce(function (s, i) { return s + i.price * i.qty; }, 0);
    var grossTotal = serviceP + productTotal;
    var depP = Number(deposit) || 0;
    var balance = Math.max(0, grossTotal - depP);

    var menuRef = menuPrice(service, props.stylist);

    function onComplete() {
      if (!service || serviceP <= 0) { alert('Add a service price.'); return; }
      if (!client.name && !client.email) { alert('Add at least a client name or email.'); return; }

      setSubmit({ status: 'sending' });
      var payload = {
        stylist: props.stylist,
        date: todayISO(),
        clientName: client.name,
        clientEmail: client.email,
        clientPhone: client.phone,
        calendarEventId: defaults.eventId || '',
        bookingRef: defaults.bookingRef || '',
        service: { type: service, price: serviceP },
        products: items.map(function (i) { return { shopifyId: i.id, name: i.name, price: i.price, qty: i.qty }; }),
        depositDeducted: depP,
        paymentMethod: paymentMethod,
        notes: notes
      };
      postJSON(WEBHOOKS.checkout, payload)
        .then(function (r) {
          setSubmit({ status: 'done' });
          props.onComplete({
            reference: r.reference,
            balanceOwed: r.balanceOwed,
            grossTotal: r.grossTotal,
            paymentStatus: r.paymentStatus,
            paymentMethod: paymentMethod,
            client: client
          });
        })
        .catch(function (err) {
          setSubmit({ status: 'error', message: err.message });
        });
    }

    return e('div', { className: 'cb-co', style: { minHeight: '100vh', background: C.cream, paddingBottom: 200 } },
      // Header
      e('div', { style: { padding: '20px 32px', borderBottom: '1px solid ' + C.line, background: '#fff' } },
        e('div', { style: { maxWidth: 1080, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 } },
          e('button', { onClick: props.onBack, className: 'btn ghost', style: { minHeight: 40, padding: '8px 16px' } }, '← Back'),
          e('div', { style: { textAlign: 'center', flex: 1 } },
            e('div', { className: 'label' }, 'Step 3 of 3'),
            e('h2', { style: { fontSize: 22, color: C.charcoal, marginTop: 4 } }, 'Build basket')
          ),
          e('div', { style: { display: 'flex', alignItems: 'center', gap: 8, fontFamily: FONT_DISPLAY, color: C.charcoal } },
            e('div', { style: { width: 10, height: 10, borderRadius: '50%', background: STYLIST_ACCENT[props.stylist] } }),
            props.stylist
          )
        )
      ),

      e('div', { style: { maxWidth: 1080, margin: '0 auto', padding: 24, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 20 } },

        // ---- Left: Client + Service ----
        e('div', null,
          e('div', { className: 'card', style: { marginBottom: 20 } },
            e('h3', { style: { fontSize: 18, color: C.charcoal, marginBottom: 14 } }, 'Client'),
            e('div', { className: 'field' },
              e('label', null, 'Name'),
              e('input', { type: 'text', value: client.name, onChange: function (ev) { setClient(Object.assign({}, client, { name: ev.target.value })); }, placeholder: 'Client name' })
            ),
            e('div', { className: 'field' },
              e('label', null, 'Email'),
              e('input', { type: 'email', value: client.email, onChange: function (ev) { setClient(Object.assign({}, client, { email: ev.target.value })); }, placeholder: 'name@example.com' })
            ),
            e('div', { className: 'field', style: { marginBottom: 0 } },
              e('label', null, 'Phone'),
              e('input', { type: 'tel', value: client.phone, onChange: function (ev) { setClient(Object.assign({}, client, { phone: ev.target.value })); }, placeholder: '07' })
            )
          ),

          e('div', { className: 'card' },
            e('h3', { style: { fontSize: 18, color: C.charcoal, marginBottom: 14 } }, 'Service'),
            e('div', { className: 'field' },
              e('label', null, 'Type'),
              e('select', { value: service, onChange: function (ev) { changeService(ev.target.value); } },
                availableServices.map(function (s) {
                  return e('option', { key: s.type, value: s.type }, s.type);
                })
              )
            ),
            e('div', { className: 'field', style: { marginBottom: 0 } },
              e('label', null, 'Price charged (£)'),
              e('input', { type: 'number', step: '0.01', min: '0', value: price, onChange: function (ev) { setPrice(ev.target.value); }, placeholder: '0.00' }),
              e('div', { className: 'help' },
                menuRef == null ? 'Menu: price on consultation' : ('Menu: from £' + menuRef + ' — adjust upward if needed')
              )
            )
          )
        ),

        // ---- Right: Products + Deposit + Notes ----
        e('div', null,
          e('div', { className: 'card', style: { marginBottom: 20 } },
            e('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 } },
              e('h3', { style: { fontSize: 18, color: C.charcoal } }, 'Products'),
              e('button', { onClick: function () { setShowPicker(true); }, className: 'btn', style: { minHeight: 40, padding: '8px 16px', fontSize: 11 } }, '+ Add Product')
            ),
            items.length === 0 && e('div', { style: { color: C.muted, fontSize: 13, fontFamily: FONT_DISPLAY, fontStyle: 'italic', padding: '8px 0' } }, 'No products added.'),
            items.length > 0 && e('div', { style: { display: 'flex', flexDirection: 'column', gap: 10 } },
              items.map(function (i) {
                return e('div', { key: i.id, style: { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderTop: '1px solid ' + C.line } },
                  i.image && e('img', { src: i.image, alt: '', style: { width: 44, height: 44, objectFit: 'cover', borderRadius: 4, background: '#f4eee9' } }),
                  e('div', { style: { flex: 1, minWidth: 0 } },
                    e('div', { style: { fontFamily: FONT_DISPLAY, fontSize: 16, color: C.charcoal, lineHeight: 1.2 } }, i.name),
                    e('div', { style: { fontSize: 12, color: C.muted, marginTop: 3 } }, fmtMoney(i.price) + ' each')
                  ),
                  e('input', { type: 'number', min: '1', value: i.qty, onChange: function (ev) { setQty(i.id, ev.target.value); }, style: { width: 60, padding: '8px 10px', border: '1px solid ' + C.line, borderRadius: 4, textAlign: 'center' } }),
                  e('div', { style: { width: 64, textAlign: 'right', fontFamily: FONT_DISPLAY, fontSize: 16, color: C.charcoal } }, fmtMoney(i.price * i.qty)),
                  e('button', { onClick: function () { removeProduct(i.id); }, style: { background: 'transparent', border: 'none', color: C.muted, fontSize: 18, padding: 6, cursor: 'pointer' }, title: 'Remove' }, '×')
                );
              })
            )
          ),

          e('div', { className: 'card' },
            e('h3', { style: { fontSize: 18, color: C.charcoal, marginBottom: 14 } }, 'Deposit & Notes'),
            e('div', { className: 'field' },
              e('label', null, 'Deposit deducted (£)'),
              e('input', { type: 'number', step: '0.01', min: '0', value: deposit, onChange: function (ev) { setDeposit(ev.target.value); } }),
              e('div', { className: 'help' }, 'Default £50. Use 0 for walk-ins with no deposit on file.')
            ),
            e('div', { className: 'field', style: { marginBottom: 0 } },
              e('label', null, 'Notes (internal)'),
              e('textarea', { value: notes, onChange: function (ev) { setNotes(ev.target.value); }, rows: 2, placeholder: 'Anything to remember about this visit…', style: { padding: 14, border: '1px solid ' + C.line, borderRadius: 4, resize: 'vertical' } })
            )
          )
        )
      ),

      // ---- Sticky summary bar ----
      e('div', { style: { position: 'fixed', left: 0, right: 0, bottom: 0, background: '#fff', borderTop: '1px solid ' + C.line, boxShadow: '0 -4px 16px rgba(74,25,66,.06)', padding: '14px 24px', zIndex: 50 } },
        e('div', { style: { maxWidth: 1080, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 18, flexWrap: 'wrap' } },
          e('div', { style: { display: 'flex', gap: 24, fontSize: 13, color: C.body, flexWrap: 'wrap' } },
            e('div', null, e('span', { style: { color: C.muted } }, 'Service '), fmtMoney(serviceP)),
            e('div', null, e('span', { style: { color: C.muted } }, 'Products '), fmtMoney(productTotal)),
            e('div', null, e('span', { style: { color: C.muted } }, 'Deposit '), '−', fmtMoney(depP)),
            e('div', { style: { fontFamily: FONT_DISPLAY, fontSize: 22, color: C.charcoal } }, e('span', { style: { fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', color: C.gold, marginRight: 8 } }, 'Balance Due'), fmtMoney(balance))
          ),
          e('div', { style: { display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' } },
            e('select', { value: paymentMethod, onChange: function (ev) { setPaymentMethod(ev.target.value); }, style: { padding: '12px 14px', border: '1px solid ' + C.line, borderRadius: 4, background: '#fff' } },
              e('option', { value: 'Bank Transfer' }, 'Bank Transfer'),
              e('option', { value: 'Cash' }, 'Cash'),
              e('option', { value: 'Card' }, 'Card')
            ),
            e('button', { onClick: onComplete, disabled: submit.status === 'sending', className: 'btn dark lg' },
              submit.status === 'sending' ? 'Saving…' : 'Complete Checkout'
            )
          )
        ),
        submit.status === 'error' && e('div', { style: { maxWidth: 1080, margin: '8px auto 0', color: C.danger, fontSize: 13 } }, 'Could not save: ' + (submit.message || 'unknown error'))
      ),

      // ---- Product picker overlay ----
      showPicker && e(ProductPicker, { onClose: function () { setShowPicker(false); }, onPick: addProduct })
    );
  }

  // ---- Product picker modal ----
  function ProductPicker(props) {
    var stQ = useState('');
    var q = stQ[0], setQ = stQ[1];
    var filtered = useMemo(function () {
      var qq = q.trim().toLowerCase();
      if (!qq) return PRODUCTS;
      return PRODUCTS.filter(function (p) { return (p.name || '').toLowerCase().indexOf(qq) >= 0 || (p.vendor || '').toLowerCase().indexOf(qq) >= 0; });
    }, [q]);

    return e('div', { onClick: props.onClose, style: { position: 'fixed', inset: 0, background: 'rgba(42,33,49,.5)', zIndex: 100, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' } },
      e('div', { onClick: function (ev) { ev.stopPropagation(); }, className: 'cb-co', style: { background: C.cream, width: '100%', maxWidth: 600, maxHeight: '90vh', borderRadius: '12px 12px 0 0', display: 'flex', flexDirection: 'column' } },
        e('div', { style: { padding: 20, borderBottom: '1px solid ' + C.line, background: '#fff', borderRadius: '12px 12px 0 0' } },
          e('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 } },
            e('h3', { style: { fontFamily: FONT_DISPLAY, fontSize: 20, color: C.charcoal } }, 'Add a product'),
            e('button', { onClick: props.onClose, style: { background: 'transparent', border: 'none', color: C.muted, fontSize: 22, padding: 4, cursor: 'pointer' } }, '×')
          ),
          e('input', { type: 'search', value: q, onChange: function (ev) { setQ(ev.target.value); }, placeholder: 'Search products…', style: { width: '100%', padding: 14, border: '1px solid ' + C.line, borderRadius: 4, background: '#fff' }, autoFocus: true })
        ),
        e('div', { style: { padding: 16, overflowY: 'auto', flex: 1 } },
          PRODUCTS.length === 0 && e('div', { style: { color: C.muted, textAlign: 'center', padding: 24, fontFamily: FONT_DISPLAY, fontStyle: 'italic' } }, 'No products in the retail collection. Add products to the “Retail” collection in Shopify admin to populate this list.'),
          filtered.length === 0 && PRODUCTS.length > 0 && e('div', { style: { color: C.muted, textAlign: 'center', padding: 24 } }, 'No matches.'),
          e('div', { style: { display: 'flex', flexDirection: 'column', gap: 8 } },
            filtered.map(function (p) {
              return e('button', { key: p.id, onClick: function () { props.onPick(p); }, style: { display: 'flex', alignItems: 'center', gap: 12, padding: 12, background: '#fff', border: '1px solid ' + C.line, borderRadius: 6, cursor: 'pointer', textAlign: 'left' } },
                p.image && e('img', { src: p.image, alt: '', style: { width: 56, height: 56, objectFit: 'cover', borderRadius: 4, background: '#f4eee9' } }),
                e('div', { style: { flex: 1, minWidth: 0 } },
                  e('div', { style: { fontFamily: FONT_DISPLAY, fontSize: 17, color: C.charcoal } }, p.name),
                  p.vendor && e('div', { style: { fontSize: 11, color: C.muted, letterSpacing: 1, textTransform: 'uppercase', marginTop: 2 } }, p.vendor)
                ),
                e('div', { style: { fontFamily: FONT_DISPLAY, fontSize: 18, color: C.gold } }, fmtMoney(Number(p.price)))
              );
            })
          )
        )
      )
    );
  }

  // =======================================================
  //   Confirmation
  // =======================================================
  function ConfirmScreen(props) {
    var r = props.result;
    var isBank = r.paymentMethod === 'Bank Transfer';
    var bank = { name: 'Catherine Boden Ltd', sort: '04-06-05', acc: '23042902' };

    return e('div', { className: 'cb-co pad', style: { minHeight: '100vh', background: C.cream, display: 'flex', alignItems: 'center', justifyContent: 'center' } },
      e('div', { style: { maxWidth: 560, width: '100%', textAlign: 'center' } },
        e('div', { style: { width: 64, height: 64, borderRadius: '50%', background: 'rgba(63,122,92,.1)', color: C.success, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32, margin: '0 auto 18px' } }, '✓'),
        e('h1', { style: { fontSize: 32, color: C.charcoal, marginBottom: 6 } }, 'Checkout ', e('em', { style: { color: C.gold, fontStyle: 'italic' } }, isBank ? 'recorded' : 'paid')),
        e('p', { style: { color: C.muted, fontFamily: FONT_DISPLAY, fontStyle: 'italic', fontSize: 15, marginBottom: 28 } },
          isBank ? 'Share these details with the client to settle their balance.' : 'Payment complete. Have a lovely day.'
        ),
        e('div', { className: 'card', style: { textAlign: 'left', marginBottom: 24 } },
          e('div', { className: 'label' }, 'Reference'),
          e('div', { style: { fontFamily: 'monospace', fontSize: 22, color: C.gold, marginTop: 4, marginBottom: 16, letterSpacing: 1 } }, r.reference),
          e('div', { className: 'label' }, 'Balance ' + (isBank ? 'due' : 'taken')),
          e('div', { style: { fontFamily: FONT_DISPLAY, fontSize: 32, color: C.charcoal, marginTop: 4 } }, fmtMoney(r.balanceOwed)),
          isBank && e('div', { style: { borderTop: '1px solid ' + C.line, marginTop: 18, paddingTop: 16 } },
            e('div', { className: 'label', style: { marginBottom: 10 } }, 'Bank details'),
            e('table', { style: { fontSize: 14, width: '100%' } },
              e('tbody', null,
                e('tr', null, e('td', { style: { color: C.muted, padding: '4px 12px 4px 0' } }, 'Account name'), e('td', { style: { color: C.charcoal } }, bank.name)),
                e('tr', null, e('td', { style: { color: C.muted, padding: '4px 12px 4px 0' } }, 'Sort code'), e('td', { style: { color: C.charcoal, fontFamily: 'monospace' } }, bank.sort)),
                e('tr', null, e('td', { style: { color: C.muted, padding: '4px 12px 4px 0' } }, 'Account no.'), e('td', { style: { color: C.charcoal, fontFamily: 'monospace' } }, bank.acc))
              )
            ),
            r.client && r.client.email && e('div', { style: { marginTop: 14, padding: 12, background: C.creamWarm, borderRadius: 4, fontSize: 13, color: C.body } }, 'Email with these details has been sent to ', e('strong', null, r.client.email), '.')
          )
        ),
        e('div', { style: { display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' } },
          e('button', { onClick: props.onAnother, className: 'btn lg' }, 'Done — Next Client'),
          e('button', { onClick: props.onSwitch, className: 'btn ghost lg' }, 'Switch Stylist')
        )
      )
    );
  }

  // =======================================================
  //   App shell
  // =======================================================
  function App() {
    var stScreen = useState('pin'); // pin | stylist | today | basket | confirm
    var screen = stScreen[0], setScreen = stScreen[1];
    var stStylist = useState(null);
    var stylist = stStylist[0], setStylist = stStylist[1];
    var stAppt = useState(null);
    var appt = stAppt[0], setAppt = stAppt[1];
    var stResult = useState(null);
    var result = stResult[0], setResult = stResult[1];

    if (screen === 'pin') return e(PinScreen, { onUnlock: function () { setScreen('stylist'); } });
    if (screen === 'stylist') return e(StylistScreen, { onPick: function (s) { setStylist(s); setScreen('today'); } });
    if (screen === 'today') return e(TodayScreen, {
      stylist: stylist,
      onPick: function (ev) { setAppt(ev); setScreen('basket'); },
      onWalkIn: function () { setAppt(null); setScreen('basket'); },
      onBack: function () { setStylist(null); setScreen('stylist'); }
    });
    if (screen === 'basket') return e(BasketScreen, {
      stylist: stylist,
      appointment: appt,
      onBack: function () { setScreen('today'); },
      onComplete: function (r) { setResult(r); setScreen('confirm'); }
    });
    if (screen === 'confirm') return e(ConfirmScreen, {
      result: result,
      onAnother: function () { setAppt(null); setResult(null); setScreen('today'); },
      onSwitch: function () { setStylist(null); setAppt(null); setResult(null); setScreen('stylist'); }
    });
    return null;
  }

  // ----- Mount -----
  function mount() {
    var root = document.getElementById('cb-salon-checkout-root');
    if (!root) { console.error('CB Checkout: root element not found'); return; }
    if (!window.React || !window.ReactDOM) { console.error('CB Checkout: React not loaded'); return; }
    ReactDOM.createRoot(root).render(e(App));
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mount);
  } else {
    mount();
  }
})();
